import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CompanyAgreementRow, CompanyDetail, CompanyRow } from '@dru/types';
import { BRAND, formatBytes } from '@dru/types';
import {
  Alert, Button, DataTable, DetailSection, Facts, IconAction, InfoTip, RowActions, TextField,
} from '@dru/ui';
import { Eye, Trash2 } from 'lucide-react';
import { ApiError } from '@dru/api-client';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';
import { formatDate } from '../../../lib/format';
import { toBase64 } from '../../../lib/files';
import { Dialog } from '../../../components/Dialog';
import { CompanySelect } from './CompanySelect';

/**
 * The company profile extras (legacy-app parity P2), on the Overview tab.
 *
 * Two sections. "Company profile" is the PAN beside the GSTIN, the group
 * company, the person at the company Dr Ultimate coordinates camps with, and
 * the numbers a venue needs on the day something goes wrong — all kept by
 * platform staff, shown read-only to HR under /hr/settings. "Agreements" is
 * the signed paperwork, held like employee documents: private storage, a
 * five-minute grant to open, every open recorded.
 *
 * Edited in a dialog of their own rather than folded into the registration
 * dialog, which is already long and asks about a different thing.
 */

const none = (text: string) => <span className="text-neutral-500">{text}</span>;

export function CompanyExtrasSection({ company: c, canEdit, onSaved }: {
  company: CompanyDetail; canEdit: boolean; onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const edit = canEdit ? (
    <button type="button" onClick={() => setEditing(true)} className="-mx-1.5 inline-flex min-h-6 items-center tap-area rounded px-1.5 text-caption font-medium text-primary-600 hover:bg-primary-50 hover:text-primary-700">
      Edit
    </button>
  ) : undefined;
  const coordinator = [c.coordinatorName, c.coordinatorDesignation].filter(Boolean).join(' · ');
  const reach = [c.coordinatorMobile, c.coordinatorAltMobile, c.coordinatorEmail].filter(Boolean).join(' · ');

  return (
    <DetailSection
      title="Company profile"
      phoneFolded={false}
      action={(
        <span className="flex items-center gap-2">
          <InfoTip label="About the company profile">
            Kept by {BRAND.name} and shown to the company's HR read-only. The coordinator is who we
            arrange camps with; the emergency numbers are for the venue on the day.
          </InfoTip>
          {edit}
        </span>
      )}
    >
      <Facts
        columns={4}
        facts={[
          { label: 'PAN', value: c.pan ? <span className="font-mono">{c.pan}</span> : none('Not recorded') },
          {
            label: 'Parent company',
            value: c.parentTenant
              ? <Link to={`/admin/companies/${c.parentTenant.id}`} className="text-primary-700 hover:underline">{c.parentTenant.name}</Link>
              : none('None — not part of a group'),
          },
          {
            label: 'Coordinator',
            value: coordinator || none('Not recorded'),
            hint: reach || undefined,
          },
          {
            label: 'Emergency numbers',
            value: c.emergencyAmbulance || c.emergencyFire || c.emergencyPolicePhone
              ? (
                <span className="block space-y-0.5">
                  <span className="block">Ambulance {c.emergencyAmbulance ? <span className="font-mono">{c.emergencyAmbulance}</span> : none('—')}</span>
                  <span className="block">Fire {c.emergencyFire ? <span className="font-mono">{c.emergencyFire}</span> : none('—')}</span>
                  <span className="block">Police {c.emergencyPolicePhone ? <span className="font-mono">{c.emergencyPolicePhone}</span> : none('—')}</span>
                </span>
              )
              : none('Not recorded'),
            hint: c.emergencyPoliceAddress ? `Police station: ${c.emergencyPoliceAddress}` : undefined,
          },
        ]}
      />
      {editing && (
        <ExtrasDialog company={c} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); onSaved(); }} />
      )}
    </DetailSection>
  );
}

const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const MOBILE_RE = /^[6-9]\d{9}$/;
const PHONE_RE = /^[0-9+()\- ]{3,20}$/;

function ExtrasDialog({ company, onClose, onSaved }: {
  company: CompanyDetail; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<Record<string, string>>(() => ({
    pan: company.pan ?? '',
    coordinatorName: company.coordinatorName ?? '',
    coordinatorDesignation: company.coordinatorDesignation ?? '',
    coordinatorEmail: company.coordinatorEmail ?? '',
    coordinatorMobile: company.coordinatorMobile ?? '',
    coordinatorAltMobile: company.coordinatorAltMobile ?? '',
    emergencyAmbulance: company.emergencyAmbulance ?? '',
    emergencyFire: company.emergencyFire ?? '',
    emergencyPolicePhone: company.emergencyPolicePhone ?? '',
    emergencyPoliceAddress: company.emergencyPoliceAddress ?? '',
  }));
  const [parentId, setParentId] = useState(company.parentTenantId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const set = (k: string) => (v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((e) => { const { [k]: _, ...rest } = e; return rest; });
  };
  const digits = (k: string) => (v: string) => set(k)(v.replace(/\D/g, '').slice(0, 10));

  // The picker needs the chosen row to show its name before a search runs;
  // only the id and the name are read from it.
  const parentRow: CompanyRow[] = company.parentTenant
    ? [{ id: company.parentTenant.id, name: company.parentTenant.name } as CompanyRow]
    : [];

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (form.pan && !PAN_RE.test(form.pan)) e.pan = 'Enter a valid PAN, such as AAACD1234F';
    if (parentId && parentId === company.id) e.parentTenantId = 'A company cannot be its own parent';
    if (form.coordinatorEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.coordinatorEmail)) e.coordinatorEmail = 'Enter a valid email address';
    if (form.coordinatorMobile && !MOBILE_RE.test(form.coordinatorMobile)) e.coordinatorMobile = 'Enter a valid mobile number';
    if (form.coordinatorAltMobile && !MOBILE_RE.test(form.coordinatorAltMobile)) e.coordinatorAltMobile = 'Enter a valid mobile number';
    for (const k of ['emergencyAmbulance', 'emergencyFire', 'emergencyPolicePhone']) {
      if (form[k] && !PHONE_RE.test(form[k])) e[k] = 'Enter a phone number';
    }
    return e;
  }

  async function save() {
    const local = validate();
    setFieldErrors(local);
    if (Object.keys(local).length) { setError('Please correct the highlighted fields.'); return; }
    setBusy(true); setError(null);
    try {
      const nullable = (k: string) => (form[k]?.trim() ? form[k].trim() : null);
      await api.patch(`/tenants/${company.id}`, {
        pan: form.pan ? form.pan.toUpperCase() : null,
        parentTenantId: parentId || null,
        coordinatorName: nullable('coordinatorName'),
        coordinatorDesignation: nullable('coordinatorDesignation'),
        coordinatorEmail: nullable('coordinatorEmail'),
        coordinatorMobile: nullable('coordinatorMobile'),
        coordinatorAltMobile: nullable('coordinatorAltMobile'),
        emergencyAmbulance: nullable('emergencyAmbulance'),
        emergencyFire: nullable('emergencyFire'),
        emergencyPolicePhone: nullable('emergencyPolicePhone'),
        emergencyPoliceAddress: nullable('emergencyPoliceAddress'),
      });
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.hasFieldErrors) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
    } finally { setBusy(false); }
  }

  return (
    <Dialog title={`Company profile — ${company.name}`} onClose={onClose} wide>
      <div className="mt-2">
        <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">Identity</h3>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <TextField label="PAN" value={form.pan} mono error={fieldErrors.pan}
            onChange={(v) => set('pan')(v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))}
            hint="Ten characters, as on the PAN card." />
          <div>
            <CompanySelect list={parentRow} value={parentId} onChange={(id) => { setParentId(id); setFieldErrors((e) => { const { parentTenantId: _, ...r } = e; return r; }); }} />
            <div className="mt-1 flex flex-wrap items-center gap-2 text-caption text-neutral-500">
              <span>Parent company — the group this one reports to, if any.</span>
              {parentId && <button type="button" className="text-primary-600 hover:underline" onClick={() => setParentId('')}>Clear</button>}
            </div>
            {fieldErrors.parentTenantId && <p className="mt-1 text-caption text-danger-600" role="alert">{fieldErrors.parentTenantId}</p>}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">Coordinator</h3>
        <p className="mt-1 text-caption text-neutral-500">Who at the company arranges camps and schedules with {BRAND.name}.</p>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <TextField label="Name" value={form.coordinatorName} error={fieldErrors.coordinatorName} onChange={set('coordinatorName')} />
          <TextField label="Designation" value={form.coordinatorDesignation} error={fieldErrors.coordinatorDesignation} onChange={set('coordinatorDesignation')} />
          <TextField label="Email" value={form.coordinatorEmail} type="email" error={fieldErrors.coordinatorEmail} onChange={set('coordinatorEmail')} />
          <TextField label="Mobile" value={form.coordinatorMobile} mono inputMode="numeric" error={fieldErrors.coordinatorMobile} onChange={digits('coordinatorMobile')} />
          <TextField label="Alternate mobile" value={form.coordinatorAltMobile} mono inputMode="numeric" error={fieldErrors.coordinatorAltMobile} onChange={digits('coordinatorAltMobile')} />
        </div>
      </div>

      <div className="mt-4">
        <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">Emergency contacts</h3>
        <p className="mt-1 text-caption text-neutral-500">For the venue on the day: the nearest ambulance, fire service and police station.</p>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <TextField label="Ambulance" value={form.emergencyAmbulance} mono inputMode="tel" error={fieldErrors.emergencyAmbulance} onChange={set('emergencyAmbulance')} placeholder="108" />
          <TextField label="Fire" value={form.emergencyFire} mono inputMode="tel" error={fieldErrors.emergencyFire} onChange={set('emergencyFire')} placeholder="101" />
          <TextField label="Police station phone" value={form.emergencyPolicePhone} mono inputMode="tel" error={fieldErrors.emergencyPolicePhone} onChange={set('emergencyPolicePhone')} />
          <TextField label="Police station address" value={form.emergencyPoliceAddress} error={fieldErrors.emergencyPoliceAddress} onChange={set('emergencyPoliceAddress')} />
        </div>
      </div>

      {error && <Alert className="mt-3">{error}</Alert>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} loading={busy}>Save profile</Button>
      </div>
    </Dialog>
  );
}

/* ── Agreements ─────────────────────────────────────────────── */

const ACCEPT = 'application/pdf,image/jpeg,image/png';
/** Under the adapter's body limit once base64-encoded, as for employee documents. */
const MAX_BYTES = 1_000_000;

const day = (d?: string | null) => (d ? formatDate(d) : '—');

export function AgreementsSection({ company: c, canEdit }: { company: CompanyDetail; canEdit: boolean }) {
  const docs = useQuery<CompanyAgreementRow[]>(() => api.get(`/tenants/${c.id}/agreements`), [c.id]);
  const [uploading, setUploading] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const [removing, setRemoving] = useState<CompanyAgreementRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const open = async (id: string) => {
    setOpening(id); setError(null);
    try {
      const grant = await api.get<{ url: string }>(`/tenants/${c.id}/agreements/${id}/download`);
      window.open(grant.url, '_blank', 'noopener,noreferrer');
    } catch (e) { setError(errorMessage(e)); } finally { setOpening(null); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <DetailSection
      title={`Agreements${docs.data ? ` (${docs.data.length})` : ''}`}
      phoneFolded={false}
      action={canEdit ? (
        <Button size="sm" variant="secondary" onClick={() => setUploading(true)}>Attach an agreement</Button>
      ) : undefined}
    >
      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}
      <DataTable<CompanyAgreementRow>
        rows={docs.data ?? []}
        loading={docs.loading}
        error={docs.error}
        onRetry={docs.reload}
        rowKey={(d) => d.id}
        dense
        empty={{
          title: 'No agreements on file',
          hint: canEdit ? 'Attach the signed service agreement, an NDA or a renewal.' : `Agreements attached by ${BRAND.name} appear here.`,
        }}
        columns={[
          {
            key: 'title', header: 'Agreement', alwaysVisible: true,
            cell: (d) => (
              <div className="min-w-0">
                <p className="truncate font-medium text-neutral-900">{d.title}</p>
                <p className="truncate text-caption font-normal text-neutral-500">{d.fileName} · {formatBytes(d.sizeBytes)}</p>
              </div>
            ),
          },
          { key: 'signed', header: 'Signed', cell: (d) => <span className="text-small text-neutral-700">{day(d.signedOn)}</span> },
          {
            key: 'valid', header: 'Valid until',
            cell: (d) => (
              <span className={d.validUntil && d.validUntil.slice(0, 10) < today ? 'text-small text-overdue-600' : 'text-small text-neutral-700'}>
                {day(d.validUntil)}{d.validUntil && d.validUntil.slice(0, 10) < today ? ' · expired' : ''}
              </span>
            ),
          },
          { key: 'added', header: 'Attached', hideOnMobile: true, cell: (d) => <span className="text-small text-neutral-600">{day(d.createdAt)}</span> },
          {
            key: 'actions', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
            cell: (d) => (
              <RowActions>
                <IconAction label={`View ${d.title}`} icon={Eye} onClick={() => open(d.id)} loading={opening === d.id} />
                {canEdit && <IconAction label={`Remove ${d.title}`} icon={Trash2} danger onClick={() => setRemoving(d)} />}
              </RowActions>
            ),
          },
        ]}
      />
      <p className="mt-3 flex items-center gap-1 text-caption text-neutral-500">
        Files are held in private storage; the link an open creates expires in five minutes, and every open is recorded.
      </p>

      {uploading && (
        <UploadAgreementDialog tenantId={c.id} onClose={() => setUploading(false)} onDone={() => { setUploading(false); docs.reload(); }} />
      )}
      {removing && (
        <RemoveAgreementDialog tenantId={c.id} doc={removing} onClose={() => setRemoving(null)} onDone={() => { setRemoving(null); docs.reload(); }} />
      )}
    </DetailSection>
  );
}

function UploadAgreementDialog({ tenantId, onClose, onDone }: { tenantId: string; onClose: () => void; onDone: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [signedOn, setSignedOn] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooBig = !!file && file.size > MAX_BYTES;
  const badDates = Boolean(signedOn && validUntil && validUntil < signedOn);
  const valid = !!file && !tooBig && !badDates && title.trim().length >= 2;

  const pick = (f: File | null) => {
    setFile(f); setError(null);
    if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').slice(0, 160));
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const content = await toBase64(file);
      await api.post(`/tenants/${tenantId}/agreements`, {
        title: title.trim(),
        signedOn: signedOn || undefined,
        validUntil: validUntil || undefined,
        fileName: file.name,
        mimeType: file.type,
        content,
      });
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog onClose={onClose} title="Attach an agreement">
      <div className="space-y-3">
        <div>
          <span className="flex items-center gap-1">
            <label htmlFor="agreement-file" className="text-caption text-neutral-700">File<span className="ml-0.5 text-danger-600" aria-hidden>*</span></label>
            <InfoTip label="About the file">PDF, JPEG or PNG, up to {formatBytes(MAX_BYTES)}.</InfoTip>
          </span>
          <input
            id="agreement-file" ref={fileRef} type="file" accept={ACCEPT}
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
            className="mt-1 block w-full text-small text-neutral-700 file:mr-3 file:rounded-btn file:border-0 file:bg-primary-600 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white"
          />
        </div>
        {tooBig && (
          <Alert tone="warning">That file is {formatBytes(file!.size)}, over the {formatBytes(MAX_BYTES)} limit. A lower-resolution scan or a split PDF will go through.</Alert>
        )}
        <TextField label="Title" required value={title} onChange={setTitle} placeholder="Service agreement 2026–27" />
        <TextField label="Signed on (optional)" type="date" value={signedOn} onChange={setSignedOn} />
        <TextField label="Valid until (optional)" type="date" value={validUntil} onChange={setValidUntil}
          error={badDates ? 'Valid-until cannot be before the signing date' : undefined} />
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || busy}>{busy ? 'Uploading…' : 'Attach'}</Button>
        </div>
      </div>
    </Dialog>
  );
}

function RemoveAgreementDialog({ tenantId, doc, onClose, onDone }: {
  tenantId: string; doc: CompanyAgreementRow; onClose: () => void; onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await api.del(`/tenants/${tenantId}/agreements/${doc.id}`, reason.trim() ? { reason: reason.trim() } : {});
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog onClose={onClose} title={`Remove “${doc.title}”?`}>
      <div className="space-y-3">
        <Alert tone="info">
          The agreement stops appearing on the company's file. It is not destroyed, and the removal is recorded.
        </Alert>
        <TextField label="Why is it being removed? (optional)" value={reason} onChange={setReason} placeholder="Superseded by the 2027 agreement" autoFocus />
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="danger" onClick={submit} disabled={busy}>{busy ? 'Removing…' : 'Remove'}</Button>
        </div>
      </div>
    </Dialog>
  );
}
